import { ErrorBox, Spinner, Title4 } from '@common/components';
import { Toggle } from '@common/components/Toggle';
import Alert from '@common/components/Alert/Alert';
import { PopperRow } from '@common/components/Popper';
import { AvailabilityForm } from '@features/DynamicPackagesConfigurationWizard/components/AvailabilityForm/AvailabilityForm';
import { useGetSpaPackageConfigGroups } from '@features/spa';
import { useTranslations } from '@lang';
import { ErrorWrapper } from '@features/checkout/pages/CheckoutBilling/components/BillingForm.styles';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { ConfigurationGroupType } from 'shared-types/SpaPackage';
import { PackagesFormValues, SelectedExperienceType } from '../../../../types';
import {
  SpaAvailabilityWrapper,
  TitleWrapper,
  ToggleWrapper,
  SpaLabel,
} from './SingleOptionExperienceTimeslotSelection.styles';
import { usePackageConfiguration } from '../../../../contexts';
import { getEffectiveExperienceDate } from '../../../../helpers/getEffectiveExperienceDate';
import { findOptionFromConfigGroups } from '../../../../helpers/findOptionFromConfigGroups';

type Props = {
  experience: SelectedExperienceType;
  configurationGroups: ConfigurationGroupType[];
  experienceIndex: number;
  guestIndex?: number;
  selectedDayNumber?: number;
  numberOfGuests?: number;
  isSameTimeForAllGuests?: boolean;
  onToggleSameTime?: () => void;
  dayPicker?: React.ReactNode;
};

export const SingleOptionExperienceTimeslotSelection: React.FC<Props> = ({
  experience,
  configurationGroups,
  experienceIndex,
  guestIndex = 0,
  selectedDayNumber,
  numberOfGuests = 1,
  isSameTimeForAllGuests = false,
  onToggleSameTime,
  dayPicker,
}) => {
  const { property, spaPackageId, spaOpenTime, spaCloseTime, journeyParams } =
    usePackageConfiguration();

  const { watch } = useFormContext<PackagesFormValues>();
  const minimumTimeslotQuantity = isSameTimeForAllGuests ? numberOfGuests : 1;
  const { t } = useTranslations();
  const delayAfter = watch(`selectedExperiences.${experienceIndex}.delayAfter`);
  const guests = watch('guests') || [];
  const guest = guests[guestIndex];

  // Use selectedDayNumber if provided by parent, otherwise fall back to experience.dayNumber
  const { formattedDate: effectiveExperienceDate } = getEffectiveExperienceDate(
    journeyParams.fromDate,
    selectedDayNumber,
    experience.dayNumber
  );

  const {
    data: configGroups,
    isFetching,
    error,
  } = useGetSpaPackageConfigGroups(
    configurationGroups,
    property.id,
    Number(spaPackageId),
    effectiveExperienceDate,
    isSameTimeForAllGuests ? numberOfGuests : 1,
    spaOpenTime,
    spaCloseTime
  );

  const option = findOptionFromConfigGroups(configGroups, Number(experience.optionId));

  if (error) {
    return (
      <ErrorWrapper>
        <ErrorBox error={{ id: 'error.reservation_failed' }} />
      </ErrorWrapper>
    );
  }

  if (isFetching) {
    return <Spinner isActive={true} />;
  }

  if (!option) {
    return (
      <PopperRow>
        <Alert content={t('common.unavailable')} />
      </PopperRow>
    );
  }

  return (
    <>
      <Spinner isActive={isFetching} isBackgroundDisabled={true} />

      <TitleWrapper>
        <Title4>{option.name}</Title4>
        {numberOfGuests > 1 && onToggleSameTime && (
          <ToggleWrapper>
            <Toggle
              input={{
                value: isSameTimeForAllGuests,
                onChange: onToggleSameTime,
              }}
              label={t('overnightPackage.spa.sameOptionForGuests')}
            />
          </ToggleWrapper>
        )}
      </TitleWrapper>
      <SpaAvailabilityWrapper
        data-testid={`single-option-selection-guest-${guestIndex}-exp-${experience.id}`}
      >
        {!isSameTimeForAllGuests && guest && (
          <SpaLabel>
            {guest.guestDetails?.title} {guest.guestDetails?.firstName}{' '}
            {guest.guestDetails?.lastName}
          </SpaLabel>
        )}
        {dayPicker}
        <AvailabilityForm
          id={experience.id}
          entityType={experience.type}
          timeslots={option.timeslots || []}
          namePrefix={`guests.${guestIndex}.experiences.${experienceIndex}`}
          guestIndex={guestIndex}
          delayAfter={delayAfter}
          dayNumber={experience.dayNumber}
          experienceIndex={experienceIndex}
          minimumTimeslotQuantity={minimumTimeslotQuantity}
          selectedDayFromParent={selectedDayNumber}
        />
      </SpaAvailabilityWrapper>
    </>
  );
};
